<template>
  <form class="weight-limit" @submit.prevent="save">
    <div class="weight-limit__heading">
      <h6 class="mb-1">Shared fallback threshold</h6>
      <span class="badge text-bg-light">Small packages</span>
    </div>
    <p class="text-muted small mb-3">
      One threshold for this customer, shared by all enabled fallback carriers. Above it, outbound small packages use fallback carriers only. This does not set a maximum weight for each carrier.
    </p>
    <label :for="inputId" class="form-label">Switch to fallback above</label>
    <div class="input-group mb-2">
      <input
        :id="inputId"
        v-model="draft"
        class="form-control"
        type="number"
        min="1"
        max="5000"
        step="1"
        placeholder="No customer limit"
        :disabled="saving"
        :aria-describedby="`${inputId}-help ${inputId}-feedback`"
        :aria-invalid="invalid || undefined"
        @input="saved = false"
      />
      <span class="input-group-text">g</span>
    </div>
    <p :id="`${inputId}-help`" class="text-muted small mb-3">
      Enter 1–5,000 g, or leave blank to use existing carrier rules. Returns are unaffected.
    </p>
    <div v-if="limit !== null && !invalid" class="weight-limit__preview mb-3">
      <div>
        <strong>Up to {{ limit.toLocaleString('en-GB') }} g</strong><span>Normal carrier selection</span>
      </div>
      <div>
        <strong>Above {{ limit.toLocaleString('en-GB') }} g</strong><span>Fallback carriers only</span>
      </div>
    </div>
    <p v-if="limit !== null" class="small text-muted">
      Fallback can change the shipping cost. If no fallback carrier is eligible, the shipment cannot be routed.
    </p>
    <div :id="`${inputId}-feedback`" aria-live="polite">
      <p v-if="invalid" class="text-danger small">Enter a whole number from 1 to 5,000 g, or clear the field.</p>
      <p v-if="error" class="text-danger small" role="alert">{{ error }}</p>
      <p v-if="saved" class="text-success small" role="status">Weight limit saved for {{ companyName }}.</p>
    </div>
    <div class="d-flex justify-content-end gap-2">
      <button type="button" class="btn btn-grey" :disabled="saving || !dirty" @click="reset">
        Discard changes
      </button>
      <button type="submit" class="btn btn-green weight-limit__save" :disabled="saving || invalid || !dirty">
        {{ saving ? 'Saving…' : 'Save weight limit' }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import type { FallbackWeightLimitProps } from '@/types/Models/Company'
import { computed, ref, watch } from 'vue'

const props = defineProps<FallbackWeightLimitProps>()
const emit = defineEmits<{ save: [value: number | null] }>()
const draft = ref<string | number>(props.modelValue ?? '')
const saved = ref(false)
const submitted = ref(false)
const limit = computed(() => (String(draft.value).trim() === '' ? null : Number(draft.value)))
const invalid = computed(
  () => limit.value !== null && (!Number.isInteger(limit.value) || limit.value < 1 || limit.value > 5000),
)
const dirty = computed(() => limit.value !== props.modelValue)
watch(
  () => props.modelValue,
  (value) => {
    draft.value = value ?? ''
  },
)
watch(
  () => props.saving,
  (value, previous) => {
    if (previous && !value && submitted.value) {
      saved.value = !props.error
      submitted.value = false
    }
  },
)
function reset() {
  draft.value = props.modelValue ?? ''
  saved.value = false
}
function save() {
  if (!invalid.value && !props.saving && dirty.value) {
    submitted.value = true
    saved.value = false
    emit('save', limit.value)
  }
}
</script>

<style scoped>
.weight-limit {
  border-top: 1px solid #dee2e6;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
}
.weight-limit__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.weight-limit__preview {
  background: #f4f7fb;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.85rem;
  font-size: 0.85rem;
}
.weight-limit__preview > div {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.4rem 1rem;
}
.weight-limit__preview > div + div {
  margin-top: 0.5rem;
}
.weight-limit__save {
  color: #fff;
  background-color: #00857e !important;
  border-color: #00857e;
}
.weight-limit__save:hover:not(:disabled),
.weight-limit__save:active:not(:disabled) {
  color: #fff;
  background-color: #006b65 !important;
  border-color: #006b65;
}
.weight-limit__save:focus-visible {
  outline: 3px solid #00857e;
  outline-offset: 3px;
}
.weight-limit__save:disabled {
  color: #59635f;
  background-color: #e8ecea !important;
  border-color: #d3dad6;
  opacity: 1;
}
</style>
